/**
 * Nhận diện khuôn mặt khi thi có webcam (TT 83 C3-b — chức năng #3 phòng ngừa gian lận).
 *
 * - `FaceViolationTracker` (thuần, test được): đổi chuỗi số-khuôn-mặt theo thời gian thành
 *   vi phạm. Không mặt liên tục ≥ NO_FACE_MS → `no_face`; ≥ 2 mặt liên tục ≥ MULTI_FACE_MS →
 *   `multiple_face`. Mỗi chuỗi liên tục chỉ báo 1 lần (không spam vi phạm).
 * - `loadFaceDetector()`: MediaPipe Face Detector, WASM + model TỰ HOST (/mediapipe/wasm,
 *   /models/...). Chạy hoàn toàn trên trình duyệt — chỉ đếm số khuôn mặt, KHÔNG gửi ảnh hay
 *   đặc trưng sinh trắc học lên server (snapshot 30 giây vẫn như cũ).
 *
 * Mức `medium`: thiếu sáng/quay đi có thể báo nhầm → chỉ là tín hiệu cho GV xem xét
 * (báo cáo nghi vấn C3-c), không tự huỷ bài.
 */

export const NO_FACE_MS = 10_000;
export const MULTI_FACE_MS = 3_000;
export const SAMPLE_INTERVAL_MS = 2_000;

export interface FaceViolation {
  type: "no_face" | "multiple_face";
  severity: "medium";
  detail: { faces: number; duration_s: number };
}

export class FaceViolationTracker {
  private state: "ok" | "none" | "multi" = "ok";
  private since = 0;
  private reported = false;

  sample(faces: number, nowMs: number): FaceViolation | null {
    const next = faces === 0 ? "none" : faces >= 2 ? "multi" : "ok";
    if (next !== this.state) {
      this.state = next;
      this.since = nowMs;
      this.reported = false;
      return null;
    }
    if (next === "ok" || this.reported) return null;
    const elapsed = nowMs - this.since;
    const threshold = next === "none" ? NO_FACE_MS : MULTI_FACE_MS;
    if (elapsed < threshold) return null;
    this.reported = true;
    return {
      type: next === "none" ? "no_face" : "multiple_face",
      severity: "medium",
      detail: { faces, duration_s: Math.round(elapsed / 1000) },
    };
  }
}

export interface FaceCounter {
  count(video: HTMLVideoElement, nowMs: number): number;
  close(): void;
}

export async function loadFaceDetector(): Promise<FaceCounter> {
  const { FaceDetector, FilesetResolver } = await import("@mediapipe/tasks-vision");
  const fileset = await FilesetResolver.forVisionTasks("/mediapipe/wasm");
  const detector = await FaceDetector.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: "/models/blaze_face_short_range.tflite" },
    runningMode: "VIDEO",
    minDetectionConfidence: 0.5,
  });
  return {
    count: (video, nowMs) => detector.detectForVideo(video, nowMs).detections.length,
    close: () => detector.close(),
  };
}
